import * as React from 'react'
import { XIcon } from 'lucide-react'
import { Dialog as SheetPrimitive } from 'radix-ui'

import {
  useSheetContainer,
  useTabOwnerId,
} from '@/components/layout/SheetContainerContext'
import { cn } from '@/lib/utils'

function Sheet({
  modal,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) {
  // When the sheet is localized into a portal container (see
  // SheetContainerContext), force `modal={false}` so Radix doesn't lock
  // pointer-events on <body>. That keeps the chat sidebar (which sits outside
  // the localized container) fully interactive while the sheet is open.
  const container = useSheetContainer()
  const resolvedModal = container !== null ? false : modal
  return (
    <SheetPrimitive.Root data-slot="sheet" modal={resolvedModal} {...props} />
  )
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  isLocalized,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay> & {
  isLocalized?: boolean
}) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        // Switch fixed→absolute when localized so the dim area is bounded by
        // the SheetContainer's parent (e.g. ContentPanelArea) instead of the
        // viewport. That keeps the chat sidebar — which sits outside the
        // container — undimmed.
        isLocalized ? 'absolute' : 'fixed',
        'inset-0 z-50 bg-black/55 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-fast data-[state=closed]:duration-quick ease-smooth-out',
        className
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left'
  showCloseButton?: boolean
}) {
  const container = useSheetContainer()
  const ownerTabId = useTabOwnerId()
  const isLocalized = container !== null
  // Localized sheets run non-modal so the chat sidebar stays interactive (see
  // <Sheet> above). Radix's DismissableLayer then treats every pointerdown
  // outside the sheet content — including clicks on that interactive chat
  // sidebar — as a dismiss request, so the sheet closes when you click away
  // to use the app. Default localized sheets to ignore outside-interaction
  // dismissal; only the backdrop, the X button, and ESC close them. A consumer
  // that genuinely wants outside-click dismiss can opt back in by passing its
  // own onInteractOutside.
  const resolvedInteractOutside =
    isLocalized && !onInteractOutside
      ? (e: Event) => e.preventDefault()
      : onInteractOutside
  // Stamp the owning tab's route onto the portaled content and its
  // backdrop/edge-shadow siblings so the layout can hide a sheet that's leaked
  // onto a different active tab (see SheetContainerContext). The siblings are
  // stamped too — otherwise an inactive tab's dim + seam shadow would leak over
  // the active tab even after its content is hidden.
  const ownerProps =
    isLocalized && ownerTabId ? { 'data-tab-owner': ownerTabId } : {}
  return (
    <SheetPortal container={container ?? undefined}>
      {!isLocalized && <SheetOverlay isLocalized={isLocalized} />}
      <SheetPrimitive.Content
        onInteractOutside={resolvedInteractOutside}
        {...ownerProps}
        data-slot="sheet-content"
        className={cn(
          // `peer` lets sibling helper divs (the localized backdrop + edge
          // shadow, rendered AFTER this Content in DOM) read Content's
          // Radix-managed `data-state` via `peer-data-[state=…]:*` so they
          // can animate in AND out alongside the sheet's slide animation.
          // Localized → absolute so the sheet anchors to the SheetContainer's
          // parent (already position: relative). Combined with overflow-hidden
          // on the parent, this clips the slide-in animation cleanly.
          'peer',
          // Below xl the chat panel is a viewport-fixed overlay at z-50 and it
          // comes AFTER this container in DOM order, so a localized sheet at
          // z-50 loses the tie and paints under it. Raise the pair (content
          // z-[55] / backdrop z-[54] below) to put the modal sheet on top; at
          // xl the panel is an inline grid column again and the original
          // z-50 / z-40 pair is correct.
          isLocalized ? 'absolute z-[55] xl:z-50' : 'fixed z-50',
          'flex flex-col gap-4 bg-background shadow-lg transition ease-smooth-out data-[state=closed]:animate-out data-[state=closed]:duration-quick data-[state=open]:animate-in data-[state=open]:duration-fast',
          side === 'right' &&
            'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
          side === 'left' &&
            'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
          side === 'top' &&
            'inset-x-0 top-0 h-auto border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
          side === 'bottom' &&
            'inset-x-0 bottom-0 h-auto border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
          className
        )}
        {...props}>
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close className="absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-secondary">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
      {isLocalized && (
        <>
          {/* Radix's Dialog.Overlay short-circuits to null when modal={false},
              and we run non-modal in the localized case so the chat sidebar
              stays interactive. Render a plain backdrop div instead — same
              visual treatment, with the fade in / fade out driven by the
              `peer` SheetContent's data-state (so the backdrop animates out
              with the sheet on close, instead of popping out instantly when
              the portal unmounts).

              Positioned `fixed` (NOT absolute) so the backdrop extends beyond
              the localized container and dims the AppSidebar rail on the
              left too — `--sheet-overlay-right` (published by AuthLayout's
              grid, 0 outside it) stops it short of the chat sidebar at xl,
              where the sidebar is an inline column. Below xl the sidebar only
              floats, so the dim runs to the right edge and covers it: a modal
              sheet owns the viewport and suspends the non-modal chat overlay
              under it (clicking the dim there closes the sheet).
              z-[54] clears the chat panel's z-50 below xl and stays under the
              SheetContent (z-[55]) so the sheet reads against the dim; at xl
              both drop back to z-40 / z-50.

              Wrapped in SheetPrimitive.Close so clicking the dim closes the
              sheet for mouse users. Kept aria-hidden (decorative dim, not
              focusable) — assistive-tech and keyboard users close via the X
              button and ESC, mirroring Radix's own modal overlay. */}
          <SheetPrimitive.Close asChild>
            <div
              {...ownerProps}
              data-slot="sheet-overlay"
              aria-hidden
              className="fixed inset-y-0 left-0 right-[var(--sheet-overlay-right,0px)] z-[54] xl:z-40 bg-black/55 peer-data-[state=open]:animate-in peer-data-[state=open]:fade-in-0 peer-data-[state=closed]:animate-out peer-data-[state=closed]:fade-out-0 peer-data-[state=open]:duration-fast peer-data-[state=closed]:duration-quick ease-smooth-out"
            />
          </SheetPrimitive.Close>
          {/* Seam shadow between the sheet's right edge and the chat sidebar.
              `hidden xl:block` because the seam only exists at xl: that is the
              one tier where the chat sidebar is an inline column butted up
              against the sheet. Below xl the dim runs to the viewport edge and
              there is no seam to shade.
              Implemented as a fixed-positioned 1px strip parked at the chat's
              left edge so its box-shadow can extend leftward over the sheet —
              `position: fixed` escapes the ContentPanelArea's overflow:hidden
              clipping, and `z-[60]` puts it above the chat sidebar which would
              otherwise cover any rightward drop shadow cast from the sheet
              itself. Fade is driven by the peer SheetContent. */}
          <div
            {...ownerProps}
            data-slot="sheet-edge-shadow"
            aria-hidden
            className="pointer-events-none hidden xl:block fixed inset-y-0 right-[var(--sheet-overlay-right,0px)] z-[60] w-px shadow-[-8px_0_24px_-4px_rgba(0,0,0,0.18)] peer-data-[state=open]:animate-in peer-data-[state=open]:fade-in-0 peer-data-[state=closed]:animate-out peer-data-[state=closed]:fade-out-0 peer-data-[state=open]:duration-fast peer-data-[state=closed]:duration-quick ease-smooth-out"
          />
        </>
      )}
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col gap-1.5 p-4', className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
